import { observer } from "mobx-react-lite";
import { CubeIcon } from "@heroicons/react/outline";
import { useMemo, useState } from "react";

import { VariableConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { custom, required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const types = [
  { value: "string", label: "String" },
  { value: "json", label: "JSON" },
] as const;

const validationSchema = createBlockSchema<VariableConfiguration>()
  .requireName()
  .addRule(required("configuration.value.type", "Type is required"))
  .addRule(
    custom(
      "configuration.value.value",
      (value, payload) => {
        const type = payload.configuration?.value?.type;
        if (!type) {
          return false;
        }

        if (type === "string") {
          return typeof value === "string" && value.trim() !== "";
        }

        if (type === "json") {
          if (typeof value === "string") {
            try {
              JSON.parse(value);
              return true;
            } catch {
              return false;
            }
          }
          return typeof value === "object" && value !== null;
        }

        return false;
      },
      "Value is required and must be valid for the selected type",
      ["configuration.value.type"],
    ),
  )
  .build();

const StaticValueBlockCore = observer(
  (_props: Omit<BlockProps<VariableConfiguration>, "DefaultFields">) => {
    const block = useBlock<VariableConfiguration>();
    const isReadonly = useIsReadonly();
    const [displayValue, setDisplayValue] = useState<string | null>(
      typeof block.configuration?.value?.value === "string"
        ? block.configuration?.value?.value
        : JSON.stringify(block.configuration?.value?.value, null, 2) || null,
    );

    const handleTypeChange = (type: "string" | "json") => {
      let value = block.configuration?.value?.value || "";

      // If switching to JSON and current value is a string, try to parse it
      if (type === "json" && typeof value === "string") {
        setDisplayValue(value as string);
        try {
          value = value.trim() ? JSON.parse(value) : {};
        } catch {
          // Keep the string value for display, but store empty object
          value = {};
        }
      }
      // If switching to string and current value is an object, stringify it
      else if (type === "string" && typeof value === "object") {
        const stringValue = JSON.stringify(value);
        setDisplayValue(stringValue);
        value = stringValue;
      } else {
        setDisplayValue(null);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let newValue: any;
      if (type === "json") {
        newValue = {
          type: "json",
          value: typeof value === "object" ? value : {},
        };
      } else {
        newValue = {
          type: "string",
          value: typeof value === "string" ? value : "",
        };
      }

      block.setConfiguration({
        value: newValue,
      });
    };

    const handleValueChange = (value: string) => {
      const type = block.configuration?.value?.type || "string";

      // Always update the display value immediately
      setDisplayValue(value);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let newValue: any;
      if (type === "json") {
        let parsedValue: object;
        try {
          parsedValue = value.trim() ? JSON.parse(value) : {};
        } catch {
          // Keep the current valid object or empty object if parsing fails
          parsedValue = (block.configuration?.value?.value as object) || {};
        }
        newValue = {
          type: "json",
          value: parsedValue,
        };
      } else {
        newValue = {
          type: "string",
          value: value,
        };
      }

      block.setConfiguration({
        value: newValue,
      });
    };

    const getDisplayValue = () => {
      // If we have a display value (user is typing), show that
      if (displayValue !== null) {
        return displayValue;
      }

      // Otherwise, show the stored value
      const configValue = block.configuration?.value?.value;
      if (configValue) {
        return typeof configValue === "object" ? JSON.stringify(configValue) : configValue;
      }

      return null;
    };

    const type = block.configuration?.value?.type || null;
    const value = getDisplayValue();

    // Check if JSON is valid when type is JSON
    const isJsonValid = useMemo(() => {
      if (type !== "json" || !value) {
        return true;
      }
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }, [type, value]);

    return (
      <SectionGroup
        title="Static Value Configuration"
        description="Configure the type and value of the static data"
        icon={<CubeIcon className="h-4 w-4" />}
      >
        <div className="space-y-4">
          <ValidatedField name="Type" path="configuration.value.type">
            <Select
              value={type || undefined}
              onValueChange={handleTypeChange}
              disabled={isReadonly}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a type..." />
              </SelectTrigger>
              <SelectContent>
                {types.map(typeOption => (
                  <SelectItem key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ValidatedField>

          {type && (
            <ValidatedField name="Value" path="configuration.value.value">
              <Textarea
                className="w-full"
                value={value || ""}
                onChange={e => handleValueChange(e.target.value)}
                disabled={isReadonly}
                placeholder={type === "json" ? '{"key": "value"}' : "Enter your static value..."}
                rows={4}
              />
              {type === "json" && value && !isJsonValid && (
                <div className="mt-1 text-xs text-red-500">
                  Invalid JSON format. Please check your syntax.
                </div>
              )}
              {type === "json" && value && isJsonValid && (
                <div className="mt-1 text-xs text-gray-500">Valid JSON ✓</div>
              )}
            </ValidatedField>
          )}
        </div>
      </SectionGroup>
    );
  },
);

export default withBlockStore<VariableConfiguration>(StaticValueBlockCore, {
  validationSchema,
});
